import React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { Controller } from 'react-hook-form';
import { Control, FieldErrors } from 'react-hook-form';
import CheckIcon from 'svg/CheckIcon';
import ChevronDown from 'svg/ChevronDown';
import classNames from 'styles/utils/classNames';

type Option = {
  value: string;
  label: string;
};

type Props = {
  label?: string;
  name: string;
  errors: FieldErrors<any>;
  control: Control<any>;
  placeholder: string;
  options: Option[];
};

export default function MultiSelect({ control, label, name, errors, options, placeholder }: Props) {
  return (
    <div className="flex flex-col">
      {label && (
        <span className="mb-2 font-bold text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {label}
        </span>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value = [] } }) => {
          return (
            <div className="w-full">
              <Listbox value={value} onChange={onChange} multiple>
                {({ open }) => (
                  <>
                    <div className="relative">
                      <Listbox.Button
                        className={classNames(
                          'input-form relative flex min-h-10 w-full flex-wrap items-center gap-1 p-2 text-left',
                        )}
                      >
                        {value.length === 0 ? (
                          <span className="text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                            {placeholder}
                          </span>
                        ) : (
                          value.map((val: string) => {
                            const item = options.find((o) => o.value === val);
                            return (
                              <span
                                key={val}
                                className="rounded-full bg-gray-200 px-2 py-1 text-sm"
                              >
                                {item?.label}
                              </span>
                            );
                          })
                        )}
                        <span className="pointer-events-none absolute inset-y-0 right-0 ml-auto flex items-center pr-3">
                          <ChevronDown className="h-4 text-gray-400" aria-hidden="true" />
                        </span>
                      </Listbox.Button>

                      <Transition
                        show={open}
                        as={React.Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-color-bg-lightmode-primary py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-color-bg-darkmode-primary sm:text-sm">
                          {options.map(({ value: val, label }) => (
                            <Listbox.Option
                              key={val}
                              className={({ active }) =>
                                classNames(
                                  active &&
                                    'bg-color-bg-lightmode-invert text-color-text-lightmode-invert dark:bg-color-bg-darkmode-invert dark:text-color-text-darkmode-invert',
                                  'relative cursor-pointer select-none px-4 py-2',
                                )
                              }
                              value={val}
                            >
                              {({ selected, active }) => (
                                <>
                                  <span
                                    className={classNames(
                                      'block',
                                      selected ? 'font-medium' : 'font-normal',
                                    )}
                                  >
                                    {label}
                                  </span>

                                  {selected && (
                                    <span
                                      className={classNames(
                                        active
                                          ? 'text-color-text-lightmode-invert dark:text-color-text-darkmode-invert'
                                          : 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                                        'absolute inset-y-0 right-0 flex items-center pr-4',
                                      )}
                                    >
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  </>
                )}
              </Listbox>
            </div>
          );
        }}
      />
      <p className="bottom-1 mt-1 text-[0.75rem] text-color-error">
        {errors?.[name]?.message as any}
      </p>
    </div>
  );
}
